import {InvalidateOptions, useQuery, useQueryClient} from '@tanstack/react-query';

import {UserIdentity} from '../../../typings/user';
import {apiClient} from '../../utils/api-client';

export interface GlobalVersionDataResponseItem {
    id: number;
    label: string;
    description: string;
    createdAt: number;
    author: UserIdentity;
    validationStatus: ValidationStatus;
    invalidDocsCount: number;
    validDocsCount: number;
}

export interface GlobalVersionData extends GlobalVersionDataResponseItem {
    type: 'global-version-data';
}

interface Props {
    shouldRefetchFrequently?: boolean;
}

function getGlobalVersionsRequest() {
    return apiClient
        .get<GlobalVersionDataResponseItem[]>({
            path: 'documents-revisions-service/api/global-versions'
        })
        .then((data) => {
            return data.map(
                (item) =>
                    // tslint:disable-next-line no-object-literal-type-assertion
                    ({
                        ...item,
                        type: 'global-version-data'
                    }) as GlobalVersionData
            );
        });
}

export function useGlobalVersionsQuery(props: Props) {
    return useQuery({
        queryKey: ['global-versions'],
        queryFn: () => getGlobalVersionsRequest(),
        refetchInterval: props.shouldRefetchFrequently ? 3000 : undefined
    });
}

export function useGlobalVersionsQueryCache() {
    const queryClient = useQueryClient();

    function invalidate() {
        queryClient.invalidateQueries({queryKey: ['global-versions']}, {
            refetchType: 'inactive'
        } as InvalidateOptions);
    }

    function setQueryData(globalVersion: GlobalVersionData) {
        const data = queryClient.getQueryData<GlobalVersionData[]>(['recents']);

        if (!data) {
            invalidate();
        } else {
            queryClient.setQueryData<GlobalVersionData[]>(['global-versions'], (currentData) => [
                globalVersion,
                ...(currentData || [])
            ]);
        }
    }

    return {invalidate, setQueryData};
}
